import { useCallback, useState } from "react";
import { useOnMount } from "~/common/hooks/useOnMount";
import type { TabDefinition } from "~/Search/pages/ProductListPage/useSearchPageTabs";

interface Config {
  defaultTab: string;
  tabContainerId: string;
  trackTabActivation?: (tab: TabDefinition) => void;
  scrollOnPageLoad: boolean;
}

export const SCROLL_ON_PAGE_LOAD_TIMEOUT = 500;

export function useTabHashLogic(tabs: Record<string, TabDefinition>, config: Config) {
  const [activeTab, setActiveTab] = useState(() => getInitialTabFromUrl(tabs, config));

  const scrollToTabContainer = useCallback((immediate?: boolean) => {
    const fn = () => document.getElementById(config.tabContainerId)?.scrollIntoView();
    if (immediate) {
      fn();
    } else {
      window.setTimeout(fn, SCROLL_ON_PAGE_LOAD_TIMEOUT);
    }
  }, []);

  useOnMount(() => {
    if (!config.scrollOnPageLoad) {
      return;
    }
    const hasHash = !!window.location.hash;
    if (hasHash) {
      scrollToTabContainer();
    }
  });

  const trackTabActivation = config.trackTabActivation;
  const onTabChange = useCallback(
    (tabId: string, shouldScroll = false) => {
      const tab = Object.values(tabs).find((tab) => tab.id === tabId)!;
      setActiveTab(tabId);
      trackTabActivation?.(tab);
      window.location.hash = tabId;
      if (shouldScroll) {
        scrollToTabContainer(true);
      }
    },
    [setActiveTab, trackTabActivation, scrollToTabContainer],
  );

  return [activeTab, onTabChange] as const;
}

function getInitialTabFromUrl(tabs: Record<string, TabDefinition>, config: Config) {
  const hash = window.location.hash
    ? decodeURI(window.location.hash.substring(1))
    : window.SCHA.pageOrTabHash && decodeURI(window.SCHA.pageOrTabHash.substring(1));

  let tabId = config.defaultTab;

  if (hash) {
    const tab = Object.values(tabs).find((tab) => tab.id === hash);
    if (tab) {
      tabId = tab.id;
    }
  }

  return tabId;
}
